import { PRODUCTION_COLOUR_SETTINGS, colourPlanForSeed } from "./avatar-colours"
import { fnv1a, mulberry32 } from "./avatar-random"

export { fnv1a, mulberry32 }

export const AVATAR_RECIPE_VERSION = "2.0.0"

/**
 * Seeds the RNG that picks palette, pattern and composition. Frozen at the
 * 1.0.0 value so a version bump re-pins the look without reshuffling any
 * drug's identity. Never change this.
 */
const AVATAR_SEED_NAMESPACE = "1.0.0"

export const PALETTE_NAMES = [
  "cobalt",
  "violet",
  "sunset",
  "lagoon",
  "verdant",
  "coral",
  "aurora",
  "orchid",
  "ember",
  "mineral",
  "marine",
  "citrus",
] as const

export const PATTERN_NAMES = ["cloud", "ribbon", "marble"] as const

export type AvatarPalette = (typeof PALETTE_NAMES)[number]
export type AvatarPattern = (typeof PATTERN_NAMES)[number]
/**
 * `generated` is the production colour source: a per-seed OKLCH ramp from
 * avatar-colours.ts. `mixed` and the named palettes are the twelve fixed
 * ramps of recipe 1.x, kept for the dev playground.
 */
export type AvatarPaletteOption = AvatarPalette | "mixed" | "generated"
export type AvatarPatternOption = AvatarPattern | "mixed"

export type AvatarOptions = {
  palette?: AvatarPaletteOption
  pattern?: AvatarPatternOption
  texture?: number
  lighting?: number
  variation?: number
}

export type AvatarRecipe = {
  version: typeof AVATAR_RECIPE_VERSION
  seedHash: string
  palette: AvatarPalette | "generated"
  pattern: AvatarPattern
  colours: readonly [string, string, string, string]
  texture: number
  lighting: number
  variation: number
  rotation: number
  focalX: number
  focalY: number
  highlightX: number
  highlightY: number
  sweep: number
  turbulenceSeed: number
}

const PALETTES: Record<AvatarPalette, readonly [string, string, string, string]> = {
  cobalt: ["#0b245c", "#2453bd", "#8768d8", "#d2c9ef"],
  violet: ["#291451", "#6540a6", "#d978a0", "#f1d5da"],
  sunset: ["#62233d", "#c64a3d", "#ef9863", "#ffe3b8"],
  lagoon: ["#082d3a", "#087e85", "#63b9a7", "#ece3c6"],
  // verdant[1] sits 18 degrees cooler than a leaf green so the mid stop never
  // reads as the alert green (#1e8e3e) beside a status pill (3a review, #4).
  verdant: ["#173d2b", "#368170", "#96b56c", "#eee5c5"],
  coral: ["#642c3b", "#c44e58", "#ef8977", "#f9d4ae"],
  aurora: ["#172d55", "#5363a9", "#51aa95", "#d7edc1"],
  orchid: ["#48224e", "#984a85", "#d185a9", "#f2ced7"],
  ember: ["#51251c", "#ad4d27", "#df8738", "#f6d781"],
  mineral: ["#26363e", "#66727e", "#a9a895", "#eee5d2"],
  marine: ["#092a53", "#0b6387", "#43a5a5", "#e2e7c8"],
  citrus: ["#42401c", "#888126", "#c5b848", "#f2e7a2"],
}

const DEFAULT_OPTIONS: Required<AvatarOptions> = {
  palette: "mixed",
  pattern: "mixed",
  texture: 0.42,
  lighting: 0.72,
  variation: 0.68,
}

/**
 * The production look, pinned. Every production surface renders with these
 * values; the playground is the only place other options are used. A change
 * here (or to the algorithm) without an AVATAR_RECIPE_VERSION bump fails
 * scripts/test-drug-avatar-fixture.ts.
 */
export const PRODUCTION_AVATAR_OPTIONS: Readonly<Required<AvatarOptions>> = Object.freeze({
  palette: "generated",
  pattern: "cloud",
  texture: 0.59,
  lighting: 1,
  variation: 0.68,
})

function clamp(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback
  return Math.min(1, Math.max(0, value))
}

function isPalette(value: unknown): value is AvatarPalette {
  return typeof value === "string" && (PALETTE_NAMES as readonly string[]).includes(value)
}

function isPattern(value: unknown): value is AvatarPattern {
  return typeof value === "string" && (PATTERN_NAMES as readonly string[]).includes(value)
}

export function normaliseAvatarOptions(options: AvatarOptions = {}): Required<AvatarOptions> {
  return {
    palette: options.palette === "mixed" || options.palette === "generated" || isPalette(options.palette) ? options.palette : DEFAULT_OPTIONS.palette,
    pattern: options.pattern === "mixed" || isPattern(options.pattern) ? options.pattern : DEFAULT_OPTIONS.pattern,
    texture: clamp(options.texture, DEFAULT_OPTIONS.texture),
    lighting: clamp(options.lighting, DEFAULT_OPTIONS.lighting),
    variation: clamp(options.variation, DEFAULT_OPTIONS.variation),
  }
}

function round(value: number): number {
  return Number(value.toFixed(3))
}

export function createAvatarRecipe(seed: string, options: AvatarOptions = {}): AvatarRecipe {
  const safeOptions = normaliseAvatarOptions(options)
  const seedHash = fnv1a(String(seed)).toString(16).padStart(8, "0")
  const random = mulberry32(fnv1a(`${AVATAR_SEED_NAMESPACE}|${String(seed)}`))
  // Draw a fixed set of slots before applying options. This keeps an identity's
  // palette, pattern and composition stable while individual controls are tuned.
  const paletteIndex = Math.floor(random() * PALETTE_NAMES.length)
  const patternIndex = Math.floor(random() * PATTERN_NAMES.length)
  const rotationUnit = random()
  const focalXUnit = random()
  const focalYUnit = random()
  const sweepUnit = random()
  const turbulenceUnit = random()
  const highlightXUnit = random()
  const highlightYUnit = random()
  const palette: AvatarPalette | "generated" = safeOptions.palette === "mixed"
    ? PALETTE_NAMES[paletteIndex]!
    : safeOptions.palette
  const pattern = safeOptions.pattern === "mixed"
    ? PATTERN_NAMES[patternIndex]
    : safeOptions.pattern
  const jitter = 7 + safeOptions.variation * 13

  return {
    version: AVATAR_RECIPE_VERSION,
    seedHash,
    palette,
    pattern,
    colours: palette === "generated" ? colourPlanForSeed(String(seed), PRODUCTION_COLOUR_SETTINGS).colours : PALETTES[palette],
    texture: safeOptions.texture,
    lighting: safeOptions.lighting,
    variation: safeOptions.variation,
    rotation: round(rotationUnit * 360),
    focalX: round(36 + (focalXUnit - 0.5) * jitter),
    focalY: round(31 + (focalYUnit - 0.5) * jitter),
    highlightX: round(25 + highlightXUnit * 28),
    highlightY: round(18 + highlightYUnit * 25),
    sweep: round(33 + sweepUnit * 26),
    turbulenceSeed: 1 + Math.floor(turbulenceUnit * 9999),
  }
}

function patternMarkup(recipe: AvatarRecipe): string {
  const { colours, pattern, sweep } = recipe
  if (pattern === "ribbon") {
    return `<g transform="rotate(${recipe.rotation} 50 50)" filter="url(#soft)">
      <path d="M-18 ${round(sweep - 18)} C18 ${round(sweep - 43)} 43 ${round(sweep + 28)} 118 ${round(sweep - 4)}" fill="none" stroke="${colours[2]}" stroke-width="27" opacity=".9"/>
      <path d="M-14 ${round(sweep + 9)} C24 ${round(sweep - 12)} 58 ${round(sweep + 34)} 116 ${round(sweep + 2)}" fill="none" stroke="${colours[3]}" stroke-width="11" opacity=".72"/>
      <path d="M-12 ${round(sweep + 30)} C31 ${round(sweep + 1)} 72 ${round(sweep + 24)} 119 ${round(sweep - 14)}" fill="none" stroke="${colours[0]}" stroke-width="18" opacity=".64"/>
    </g>`
  }
  if (pattern === "marble") {
    return `<g transform="rotate(${recipe.rotation} 50 50)" filter="url(#soft)">
      <path d="M-8 20 C20 -5 29 51 56 29 S82 5 111 21" fill="none" stroke="${colours[3]}" stroke-width="14" opacity=".78"/>
      <path d="M-5 48 C24 21 37 78 65 50 S88 26 109 42" fill="none" stroke="${colours[2]}" stroke-width="21" opacity=".84"/>
      <path d="M-9 77 C22 51 42 102 69 73 S92 52 112 61" fill="none" stroke="${colours[0]}" stroke-width="14" opacity=".66"/>
    </g>`
  }
  return `<g filter="url(#cloud)">
    <ellipse cx="${round(recipe.focalX - 13)}" cy="${round(recipe.focalY + 18)}" rx="34" ry="26" fill="${colours[2]}" opacity=".9"/>
    <ellipse cx="${round(recipe.focalX + 24)}" cy="${round(recipe.focalY + 5)}" rx="38" ry="31" fill="${colours[1]}" opacity=".82"/>
    <ellipse cx="${round(recipe.focalX + 2)}" cy="${round(recipe.focalY - 15)}" rx="31" ry="24" fill="${colours[3]}" opacity=".78"/>
    <ellipse cx="${round(recipe.focalX - 3)}" cy="${round(recipe.focalY + 50)}" rx="45" ry="24" fill="${colours[0]}" opacity=".58"/>
  </g>`
}

export function avatarRecipeToSvg(recipe: AvatarRecipe): string {
  const lightOpacity = round(0.06 + recipe.lighting * 0.2)
  const grainOpacity = round(recipe.texture * 0.34)
  const frequency = round(0.48 + recipe.variation * 0.34)
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" role="img" aria-label="Procedural drug avatar">
  <defs>
    <clipPath id="orb"><circle cx="50" cy="50" r="49"/></clipPath>
    <radialGradient id="base" cx="${recipe.focalX}%" cy="${recipe.focalY}%" r="72%"><stop offset="0" stop-color="${recipe.colours[2]}"/><stop offset=".48" stop-color="${recipe.colours[1]}"/><stop offset="1" stop-color="${recipe.colours[0]}"/></radialGradient>
    <radialGradient id="shine" cx="${recipe.highlightX}%" cy="${recipe.highlightY}%" r="45%"><stop offset="0" stop-color="#fff" stop-opacity="${lightOpacity}"/><stop offset=".48" stop-color="#fff" stop-opacity=".035"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>
    <radialGradient id="limb" cx="48%" cy="43%" r="62%"><stop offset="70%" stop-color="#101728" stop-opacity="0"/><stop offset="100%" stop-color="#101728" stop-opacity=".32"/></radialGradient>
    <filter id="soft" x="-25%" y="-25%" width="150%" height="150%"><feGaussianBlur stdDeviation="${round(1.7 + recipe.variation * 2.2)}"/></filter>
    <filter id="cloud" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="${round(4.2 + recipe.variation * 3)}"/></filter>
    <filter id="grain" x="-10%" y="-10%" width="120%" height="120%"><feTurbulence type="fractalNoise" baseFrequency="${frequency}" numOctaves="3" seed="${recipe.turbulenceSeed}" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/><feComponentTransfer><feFuncA type="table" tableValues="0 ${grainOpacity}"/></feComponentTransfer></filter>
  </defs>
  <g clip-path="url(#orb)"><rect width="100" height="100" fill="url(#base)"/>${patternMarkup(recipe)}<rect width="100" height="100" fill="url(#shine)"/><rect width="100" height="100" filter="url(#grain)" opacity=".72"/><rect width="100" height="100" fill="url(#limb)"/></g>
</svg>`
}

export function avatarSvgToDataUri(svg: string): string {
  // encodeURIComponent leaves ( and ) alone; an unquoted CSS url(...) drops
  // such a value silently, so encode them too.
  return `data:image/svg+xml,${encodeURIComponent(svg).replace(/\(/g, "%28").replace(/\)/g, "%29")}`
}

export type AvatarAccentColor = {
  /** Mid stop of the ramp (colours[1]): the sphere's dominant colour, a chip fill beside it. */
  fill: string
  /** Darkest stop (colours[0]): text and monogram ink on a light ground. */
  ink: string
  /** Lightest stop (colours[3]): tints and gradient starts. */
  soft: string
}

/**
 * The sphere's accent for a seed, with no SVG render. Reads the same ramp the
 * sphere paints with, so a chip beside an avatar wears the avatar's own
 * colours. Identity-only: the ramp ignores texture, lighting and variation,
 * so the triple is stable across every option except the version. Generated
 * ramps are unique per seed, so the stops are used as-is.
 */
export function avatarAccentColor(seed: string): AvatarAccentColor {
  const { colours } = createAvatarRecipe(seed, PRODUCTION_AVATAR_OPTIONS)
  return { fill: colours[1], ink: colours[0], soft: colours[3] }
}

export function generateDrugAvatar(seed: string, options: AvatarOptions = {}): {
  recipe: AvatarRecipe
  svg: string
  dataUri: string
} {
  const recipe = createAvatarRecipe(seed, options)
  const svg = avatarRecipeToSvg(recipe)
  return { recipe, svg, dataUri: avatarSvgToDataUri(svg) }
}

export const _internals = {
  fnv1a,
  mulberry32,
  palettes: PALETTES,
}
